// Professional Loading & Skeleton System
import React from 'react';
import { cn } from '@utils/classNames';

interface LoadingProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
}

export const Loading: React.FC<LoadingProps> = ({ 
  size = 'md', 
  className,
  text,
  variant = 'spinner'
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3 border-2',
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-[3px]',
    lg: 'w-12 h-12 border-4',
    xl: 'w-16 h-16 border-4',
  };

  if (variant === 'dots') {
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        <div className="w-2.5 h-2.5 bg-gradient-to-br from-primary to-primary/60 rounded-full animate-bounce [animation-delay:-0.3s] shadow-lg shadow-primary/50" />
        <div className="w-2.5 h-2.5 bg-gradient-to-br from-primary to-primary/60 rounded-full animate-bounce [animation-delay:-0.15s] shadow-lg shadow-primary/50" />
        <div className="w-2.5 h-2.5 bg-gradient-to-br from-primary to-primary/60 rounded-full animate-bounce shadow-lg shadow-primary/50" />
        {text && <p className="ml-2 text-white/60 text-sm font-inter animate-pulse">{text}</p>}
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
        <div className="relative">
          <div className={cn('absolute inset-0 rounded-full bg-primary/30 animate-pulse-ring', sizeClasses[size])} />
          <div className={cn('relative rounded-full bg-gradient-to-br from-primary/40 to-primary/20 animate-pulse', sizeClasses[size])} />
        </div>
        {text && <p className="text-white/60 text-sm font-inter animate-pulse">{text}</p>}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <div className="relative">
        {/* Outer glow ring */}
        <div className={cn('absolute inset-0 rounded-full bg-primary/20 animate-pulse-ring blur-sm', sizeClasses[size])} />
        {/* Main spinner with gradient */}
        <div
          className={cn(
            'relative rounded-full border-4 border-transparent bg-gradient-to-r from-primary via-primary/60 to-primary animate-gradient-spin',
            sizeClasses[size]
          )}
          style={{
            background: 'conic-gradient(from 0deg, rgba(48, 216, 112, 0.1), rgba(0, 255, 97, 0.6), rgba(48, 216, 112, 0.1))',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
          }}
        />
        {/* Inner spinner */}
        <div
          className={cn(
            'absolute inset-1 rounded-full border-2 border-primary/30 border-t-primary animate-spin-slow',
            sizeClasses[size]
          )}
        />
      </div>
      {text && (
        <p className="text-white/60 text-sm font-inter animate-pulse">{text}</p>
      )}
    </div>
  );
};

// Advanced Skeleton loader
interface SkeletonProps {
  className?: string;
  count?: number;
  variant?: 'default' | 'shimmer' | 'wave';
  width?: string;
  height?: string;
  circle?: boolean;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className, 
  count = 1,
  variant = 'shimmer',
  width,
  height,
  circle = false,
  rounded = 'lg',
  style
}) => {
  const roundedClasses = {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  };

  const baseClasses = cn(
    'bg-gradient-to-r from-white/5 via-white/10 to-white/5',
    'bg-[length:200%_100%]',
    circle ? 'rounded-full' : roundedClasses[rounded],
    variant === 'shimmer' && 'animate-shimmer',
    variant === 'wave' && 'animate-wave',
    variant === 'default' && 'animate-pulse',
    'box-content',
    'min-w-0',
    // Automatically add max-w-full if width is set (to prevent overflow)
    width && !circle && !className?.includes('max-w-') ? 'max-w-full' : '',
    className
  );

  const skeletonStyle: React.CSSProperties = {
    width: circle && height ? height : width,
    height,
    maxWidth: width && !circle ? '100%' : undefined,
    boxSizing: 'border-box',
    overflow: 'hidden',
    ...style,
  };

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={baseClasses}
          style={skeletonStyle}
        />
      ))}
    </>
  );
};

// Skeleton variants for specific use cases
export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('p-4 space-y-3 bg-dark-secondary/40 border border-white/5 rounded-xl', className)}>
    <div className="flex items-center gap-3">
      <Skeleton circle width="48px" height="48px" />
      <div className="flex-1 space-y-2">
        <Skeleton height="16px" width="60%" />
        <Skeleton height="12px" width="40%" />
      </div>
    </div>
    <Skeleton height="80px" />
    <div className="flex gap-2">
      <Skeleton height="24px" width="60px" rounded="full" />
      <Skeleton height="24px" width="60px" rounded="full" />
      <Skeleton height="24px" width="60px" rounded="full" />
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; className?: string }> = ({ rows = 5, className }) => (
  <div className={cn('space-y-3', className)}>
    {/* Header */}
    <div className="flex gap-4 pb-3 border-b border-white/10">
      <Skeleton width="40%" height="16px" />
      <Skeleton width="20%" height="16px" />
      <Skeleton width="20%" height="16px" />
      <Skeleton width="20%" height="16px" />
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex gap-4 items-center py-3">
        <Skeleton circle width="40px" height="40px" />
        <Skeleton width="35%" height="14px" />
        <Skeleton width="15%" height="14px" />
        <Skeleton width="15%" height="14px" />
        <Skeleton width="15%" height="14px" />
      </div>
    ))}
  </div>
);

export const SkeletonFileGrid: React.FC<{ count?: number; className?: string }> = ({ count = 6, className }) => (
  <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4', className)}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

export const SkeletonDashboard: React.FC = () => (
  <div className="space-y-4 md:space-y-6 lg:space-y-8 w-full min-w-0 overflow-x-hidden" style={{ minWidth: 0 }}>
    {/* Page Header */}
    <div className="mb-3 md:mb-4">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton height="32px" width="140px" className="mb-1" />
          <Skeleton height="20px" width="240px" className="max-w-full" />
        </div>
      </div>
    </div>

    {/* Quick Actions */}
    <div>
      <Skeleton height="24px" width="120px" className="mb-3 md:mb-4" />
      <div className="flex flex-row flex-wrap items-center gap-1.5 md:gap-2 quick-actions-grid">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="inline-flex shrink-0 h-7 md:h-8 w-[5.25rem] sm:w-[6rem] md:w-[6.75rem] bg-gradient-to-br from-[#30d870]/80 to-[#00ff61]/80 border border-[#30d870]/25 rounded-md py-1 px-2 items-center justify-center gap-1"
          >
            <Skeleton circle width="14px" height="14px" className="bg-white/30 shrink-0" />
            <Skeleton height="10px" width="48%" className="bg-white/25 rounded shrink-0" />
          </div>
        ))}
      </div>
    </div>

    {/* Main Content Grid - Responsive */}
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_450px] gap-4 md:gap-6">
      {/* Left Column */}
      <div className="space-y-4 md:space-y-6">
        {/* Recent Files */}
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl p-4 md:p-6">
          <Skeleton height="24px" width="120px" className="mb-4" />
          <div className="space-y-3">
            {/* Table Header */}
            <div className="hidden md:flex gap-4 pb-3 border-b border-white/10">
              <Skeleton width="35%" height="16px" />
              <Skeleton width="15%" height="16px" />
              <Skeleton width="15%" height="16px" />
              <Skeleton width="15%" height="16px" />
              <Skeleton width="20%" height="16px" />
            </div>
            {/* Rows */}
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center py-2 md:py-3 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3 flex-1 w-full">
                  <Skeleton circle width="40px" height="40px" />
                  <div className="flex-1 space-y-2">
                    <Skeleton height="16px" width="60%" />
                    <div className="flex gap-2">
                      <Skeleton height="12px" width="50px" rounded="full" />
                      <Skeleton height="12px" width="40px" rounded="full" />
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex gap-4 flex-1">
                  <Skeleton height="14px" width="15%" />
                  <Skeleton height="14px" width="15%" />
                  <Skeleton height="14px" width="15%" />
                </div>
                <Skeleton height="14px" width="80px" className="md:w-auto" />
              </div>
            ))}
          </div>
        </div>

        {/* Stats - Card with grid layout */}
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl p-4 md:p-5">
          <Skeleton height="24px" width="100px" className="mb-3 md:mb-4" />
          
          {/* File Types Section */}
          <div className="mb-4 md:mb-6">
            <Skeleton height="14px" width="80px" className="mb-2 md:mb-3" />
            <div className="grid grid-cols-3 md:grid-cols-1 gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Skeleton circle width="20px" height="20px" />
                    <div className="flex-1">
                      <Skeleton height="20px" width="60%" />
                      <Skeleton height="12px" width="40%" className="mt-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Metrics Section */}
          <div>
            <Skeleton height="14px" width="80px" className="mb-2 md:mb-3" />
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 md:p-4">
                  <div className="flex items-start justify-between mb-2">
                    <Skeleton circle width="24px" height="24px" />
                  </div>
                  <Skeleton height="28px" width="80%" className="mb-1" />
                  <Skeleton height="14px" width="60%" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-4 md:space-y-6">
        {/* Ticket Stats */}
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl p-4 md:p-5">
          <Skeleton height="24px" width="140px" className="mb-4" />
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-lg p-3"
              >
                <Skeleton height="12px" width="50px" className="mb-2" />
                <Skeleton height="24px" width="40px" />
              </div>
            ))}
          </div>
        </div>

        {/* News */}
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <Skeleton height="24px" width="100px" />
            <Skeleton height="20px" width="60px" rounded="full" />
          </div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2 pb-4 border-b border-white/5 last:border-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <Skeleton circle width="20px" height="20px" />
                  <Skeleton height="16px" width="80%" />
                </div>
                <Skeleton height="14px" width="100%" />
                <Skeleton height="14px" width="60%" />
              </div>
            ))}
          </div>
        </div>

        {/* Latest Changes */}
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl p-4 md:p-5">
          <Skeleton height="24px" width="140px" className="mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton circle width="32px" height="32px" />
                <div className="flex-1 space-y-2">
                  <Skeleton height="14px" width="70%" />
                  <Skeleton height="12px" width="50px" rounded="full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const SkeletonAudioPlayer: React.FC = () => (
  <div className="space-y-4 p-6 bg-dark-secondary/40 rounded-xl">
    <div className="flex items-center gap-4">
      <Skeleton circle width="56px" height="56px" />
      <div className="flex-1 space-y-2">
        <Skeleton height="20px" width="60%" />
        <Skeleton height="14px" width="40%" />
      </div>
    </div>
    <Skeleton height="100px" rounded="lg" />
    <div className="flex items-center gap-4">
      <Skeleton circle width="48px" height="48px" />
      <Skeleton height="14px" width="80px" />
      <div className="flex-1" />
      <Skeleton width="120px" height="8px" />
    </div>
  </div>
);

export const SkeletonSupport: React.FC = () => (
  <div className="space-y-4 md:space-y-6 animate-fade-in w-full min-w-0 overflow-x-hidden">
    {/* Header */}
    <div className="mb-3 md:mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-2">
          <Skeleton height="32px" width="150px" />
          <Skeleton height="20px" width="300px" className="max-w-full" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton height="40px" width="100px" rounded="lg" />
          <Skeleton height="40px" width="120px" rounded="xl" />
        </div>
      </div>
    </div>

    {/* Filters */}
    <div className="bg-dark-secondary/50 border border-white/10 rounded-xl p-4">
      <div className="flex flex-col md:flex-row gap-3">
        <Skeleton height="40px" width="100%" rounded="lg" />
        <Skeleton height="40px" width="150px" rounded="lg" />
        <Skeleton height="40px" width="150px" rounded="lg" />
      </div>
    </div>

    {/* Tickets List */}
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-dark-secondary/50 border border-white/10 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton circle width="40px" height="40px" />
                <div className="flex-1 space-y-2">
                  <Skeleton height="18px" width="60%" />
                  <Skeleton height="14px" width="40%" />
                </div>
              </div>
              <Skeleton height="14px" width="100%" />
              <Skeleton height="14px" width="80%" />
            </div>
            <div className="flex flex-col gap-2">
              <Skeleton height="24px" width="100px" rounded="full" />
              <Skeleton height="20px" width="80px" rounded="full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonUploads: React.FC = () => (
  <div className="rounded-xl border border-white/10 bg-dark-secondary/30 backdrop-blur-md p-4 md:p-5 space-y-4 md:space-y-6 w-full min-w-0 overflow-x-hidden shadow-lg">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2 flex-1">
        <Skeleton height="28px" width="140px" />
        <Skeleton height="14px" width="200px" className="max-w-full" />
      </div>
      <Skeleton height="44px" width="88px" rounded="lg" />
    </div>

    <div className="flex flex-col gap-3">
      <Skeleton height="48px" width="100%" rounded="lg" />
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height="44px" width="72px" rounded="lg" className="flex-shrink-0" />
        ))}
      </div>
    </div>

    <div className="grid gap-3 md:gap-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-3 md:p-4 rounded-xl bg-dark-secondary/40 border border-white/10"
        >
          <div className="w-full sm:w-[120px] sm:shrink-0 aspect-video rounded-lg bg-white/5 animate-pulse" />
          <div className="flex-1 space-y-2.5 min-w-0">
            <Skeleton height="18px" width="75%" />
            <Skeleton height="14px" width="50%" />
            <div className="flex gap-2">
              <Skeleton height="22px" width="56px" rounded="lg" />
              <Skeleton height="22px" width="48px" rounded="lg" />
            </div>
            <Skeleton height="12px" width="40%" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonYouTubeConnect: React.FC = () => (
  <div className="bg-dark-secondary/50 border border-white/10 rounded-xl p-4 md:p-6">
    <div className="flex items-center gap-4">
      <Skeleton circle width="48px" height="48px" />
      <div className="flex-1 space-y-2">
        <Skeleton height="20px" width="200px" className="max-w-full" />
        <Skeleton height="16px" width="150px" />
      </div>
      <Skeleton height="40px" width="120px" rounded="lg" />
    </div>
  </div>
);

export const SkeletonSharing: React.FC = () => (
  <div className="w-full min-w-0 space-y-4 md:space-y-6 overflow-x-hidden">
    {/* Header */}
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="space-y-1">
        <Skeleton height="28px" width="140px" className="mb-1 max-w-full" />
        <Skeleton height="18px" width="260px" className="max-w-full" />
      </div>
      <Skeleton height="44px" width="110px" rounded="xl" className="w-full sm:w-auto" />
    </div>

    {/* Beat Packs Grid - Responsive */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-dark-secondary/50 border border-white/10 rounded-xl p-5 sm:p-6 hover:border-primary/50 transition-all min-w-0">
          {/* Header */}
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-4">
            <div className="flex-1 space-y-2 min-w-0">
              <Skeleton height="20px" width="70%" className="max-w-full" />
              <Skeleton height="14px" width="100%" />
              <Skeleton height="14px" width="80%" />
            </div>
            <Skeleton height="40px" width="40px" rounded="lg" className="flex-shrink-0" />
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 pb-4 border-b border-white/10">
            <div className="flex items-center gap-1.5">
              <Skeleton circle width="16px" height="16px" />
              <Skeleton height="16px" width="50px" />
            </div>
            <div className="flex items-center gap-1.5">
              <Skeleton circle width="16px" height="16px" />
              <Skeleton height="16px" width="40px" />
            </div>
            <div className="flex items-center gap-1.5">
              <Skeleton circle width="16px" height="16px" />
              <Skeleton height="16px" width="40px" />
            </div>
          </div>

          {/* Action Buttons - Compact */}
          <div className="space-y-2">
            <Skeleton height="36px" width="100%" rounded="lg" className="min-w-0" />
            <Skeleton height="36px" width="100%" rounded="lg" className="min-w-0" />
            <Skeleton height="36px" width="100%" rounded="lg" className="min-w-0" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonSettings: React.FC = () => (
  <div className="w-full min-w-0 overflow-x-hidden space-y-4 md:space-y-6">
    <div className="space-y-2">
      <Skeleton height="32px" width="140px" />
      <Skeleton height="20px" width="min(320px, 100%)" className="max-w-full" />
    </div>

    <div className="flex lg:hidden items-center gap-2 overflow-x-auto pb-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} height="44px" width="96px" rounded="xl" />
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 lg:gap-6">
      <div className="hidden lg:block">
        <div className="rounded-xl border border-white/10 bg-dark-secondary/30 p-3 sticky top-4 space-y-4">
          {Array.from({ length: 3 }).map((_, g) => (
            <div key={g} className="space-y-2">
              <Skeleton height="10px" width="80px" className="mx-2" />
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} height="44px" width="100%" rounded="lg" />
              ))}
            </div>
          ))}
          <Skeleton height="44px" width="100%" rounded="lg" />
        </div>
      </div>

      <div className="space-y-4">
        <Skeleton height="24px" width="160px" />
        <Skeleton height="14px" width="min(280px, 100%)" className="max-w-full" />
        <div className="rounded-xl border border-white/10 bg-dark-secondary/30 p-4 space-y-0 divide-y divide-white/10">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-4 py-4">
              <div className="flex-1 space-y-2">
                <Skeleton height="14px" width="140px" />
                <Skeleton height="12px" width="200px" className="max-w-full" />
              </div>
              <Skeleton height="24px" width="44px" rounded="full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const SkeletonBilling: React.FC = () => (
  <div className="min-h-screen pb-20">
    <div className="mx-auto max-w-6xl px-5 py-12 md:px-8">
      {/* Header */}
      <header className="mb-10">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Skeleton height="28px" width="160px" rounded="full" />
        </div>
        <Skeleton height="48px" width="400px" className="mb-3 max-w-full" />
        <Skeleton height="24px" width="500px" className="max-w-full" />
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        {/* Left Column */}
        <section className="space-y-6">
          {/* Current Plan Card */}
          <div className="relative overflow-hidden border-0 bg-white/[0.04] rounded-xl p-6 shadow-none">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex-1">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Skeleton height="24px" width="100px" rounded="full" />
                  <Skeleton height="24px" width="120px" rounded="full" />
                </div>
                <Skeleton height="36px" width="200px" className="mb-2 max-w-full" />
                <Skeleton height="20px" width="300px" className="mb-6 max-w-full" />
                <div className="flex items-end gap-3">
                  <Skeleton height="48px" width="120px" />
                  <Skeleton height="20px" width="180px" className="mb-2" />
                </div>
              </div>
              <div className="flex w-full flex-col gap-3 md:w-auto md:min-w-[220px]">
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                  <Skeleton height="12px" width="100px" className="mb-2" />
                  <Skeleton height="20px" width="120px" />
                </div>
                <Skeleton height="44px" width="100%" rounded="xl" />
                <Skeleton height="44px" width="100%" rounded="xl" />
              </div>
            </div>
          </div>

          {/* Storage & Highlights Card */}
          <div className="border-0 bg-white/[0.04] rounded-xl p-6 shadow-none">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                {/* Storage */}
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 sm:flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <Skeleton circle width="40px" height="40px" />
                    <div>
                      <Skeleton height="12px" width="100px" className="mb-2" />
                      <Skeleton height="24px" width="150px" />
                    </div>
                  </div>
                  <Skeleton height="8px" width="100%" rounded="full" className="mb-2" />
                  <Skeleton height="14px" width="200px" className="max-w-full" />
                </div>

                {/* Plan Highlights */}
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 sm:flex-1">
                  <Skeleton height="12px" width="120px" className="mb-3" />
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Skeleton circle width="16px" height="16px" />
                        <div className="flex-1 space-y-1">
                          <Skeleton height="16px" width="80%" />
                          <Skeleton height="12px" width="60%" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Upcoming Charge */}
              <div className="rounded-xl border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] p-4 lg:w-64">
                <Skeleton height="12px" width="120px" className="mb-2" />
                <Skeleton height="32px" width="100px" className="mb-2" />
                <Skeleton height="16px" width="140px" />
                <Skeleton height="20px" width="110px" className="mt-3" />
              </div>
            </div>
          </div>

          {/* Invoices Card */}
          <div className="overflow-hidden border-0 bg-white/[0.04] rounded-xl shadow-none">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-3">
                <Skeleton circle width="20px" height="20px" />
                <Skeleton height="24px" width="200px" className="max-w-full" />
              </div>
              <Skeleton height="36px" width="100px" rounded="lg" />
            </div>
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 border border-white/10 rounded-lg">
                  <div className="flex items-center gap-4">
                    <Skeleton circle width="40px" height="40px" />
                    <div className="space-y-2">
                      <Skeleton height="16px" width="150px" />
                      <Skeleton height="14px" width="120px" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Skeleton height="20px" width="80px" />
                    <Skeleton height="24px" width="80px" rounded="full" />
                    <Skeleton height="32px" width="32px" rounded="lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Right Column */}
        <aside className="space-y-6">
          {/* Payment Method Card */}
          <div className="border-0 bg-white/[0.04] rounded-xl p-6 shadow-none">
            <Skeleton height="24px" width="160px" className="mb-4" />
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 border border-white/10 rounded-lg">
                <Skeleton circle width="32px" height="32px" />
                <div className="flex-1 space-y-2">
                  <Skeleton height="16px" width="120px" />
                  <Skeleton height="14px" width="100px" />
                </div>
                <Skeleton height="32px" width="80px" rounded="lg" />
              </div>
              <Skeleton height="44px" width="100%" rounded="xl" />
            </div>
          </div>

          {/* Billing History Card */}
          <div className="border-0 bg-white/[0.04] rounded-xl p-6 shadow-none">
            <Skeleton height="24px" width="140px" className="mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
                  <div className="space-y-1">
                    <Skeleton height="14px" width="120px" />
                    <Skeleton height="12px" width="100px" />
                  </div>
                  <Skeleton height="16px" width="60px" />
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
);

// Full page loading with enhanced animation
export const PageLoading: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-dark/90 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in">
      <div className="flex flex-col items-center justify-center gap-6">
        {/* Enhanced spinner with multiple rings */}
        <div className="relative">
          {/* Outer pulsing ring */}
          <div className="absolute inset-0 w-20 h-20 rounded-full bg-primary/10 animate-pulse-ring blur-md" />
          {/* Middle ring */}
          <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary/60 animate-spin-slow" />
          {/* Main spinner */}
          <div className="relative w-12 h-12 rounded-full border-4 border-transparent bg-gradient-to-r from-primary via-primary/60 to-primary animate-gradient-spin"
            style={{
              background: 'conic-gradient(from 0deg, rgba(48, 216, 112, 0.2), rgba(0, 255, 97, 0.8), rgba(48, 216, 112, 0.2))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
            }}
          />
        </div>
        {/* Loading text with shimmer effect */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-white/80 text-base font-inter font-medium bg-gradient-to-r from-white via-primary/80 to-white bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer-loading">
            {text}
          </p>
          {/* Progress dots */}
          <div className="flex gap-1.5">
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline loading (for buttons, etc.)
export const InlineLoading: React.FC<{ className?: string; variant?: 'spinner' | 'dots' }> = ({ className, variant = 'spinner' }) => (
  <Loading size="xs" variant={variant} className={className} />
);

